import { HeaderContainer, HeaderIcons, HeaderLink, HeaderLinkContainer, HeaderLinkIcon, HeaderLinkText } from './styles';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import { useAuth } from '../../context/AuthContext';
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { UnstyledButton } from '../common/Button';
import ReactDOM, { createPortal } from 'react-dom';

import VipIcon from '../Icons/VipIcons';
import ProfileImage from '../common/ProfileImage';
import MoreMenu from '../MoreMenu/MoreMenu';
import CreatePost from '../Feed/CreatePost';
import VibeComposer from '../Vibes/VibeComposer';
import NotificationBadge from '../NotificationBadge';
import MessageBadge from '../MessageBadge';
import { v } from '../../styles/variables';
import { messageService } from '../../services/messageService';
import { toast } from '../../utils/notify';
import { SINGLE_POST_PAGE_URL, HELP_DESK_URL, MESSAGE_PAGE_URL, HOME_PAGE_URL, SEARCH_PAGE_URL, NOTIFICATIONS_PAGE_URL, CIRCLES_PAGE_URL, SETTINGS_PAGE_URL, DASHBOARD_PAGE_URL, PURCHASES_PAGE_URL, SCHEDULED_PAGE_URL, SUBSCRIPTIONS_PAGE_URL, MEDIA_LIBRARY_PAGE_URL, CATEGORIES_PAGE_URL } from '../../constants/systemConstants';

const MoreButton = styled(UnstyledButton)`
  /* Hidden on desktop since all items are shown in sidebar */
  @media(min-width: 769px) {
    display: none !important;
  }

  /* Mobile bottom navigation */
  @media(max-width: 768px) {
    background: ${({theme, $isActive}) => (!$isActive ? `transparent` : theme.colors.bg3)};
    border: none;
    border-radius: 50px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: inherit;
    
    &:hover {
      box-shadow: none;
    }
  }
`;

const CreateButton = styled(UnstyledButton)`
  background: ${({theme}) => theme.colors?.primary || '#007bff'};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 10px;
  color: white;
  font-weight: 500;
  font-size: 13px;
  transition: all 0.3s ease;
  margin-bottom: 2px;
  width: 100%;
  box-sizing: border-box;
  
  /* When sidebar is hovered (expanded), show normal layout */
  [data-sidebar-expanded="true"] & {
    padding: 10px 16px;
    justify-content: flex-start;
    gap: 8px;
  }
  
  &:hover {
    background: ${({theme}) => theme.colors?.primaryDark || '#0056b3'};
    transform: translateX(2px);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  /* Mobile bottom navigation */
  @media(max-width: 768px) {
    background: transparent;
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    flex-direction: column;
    gap: 2px;
    color: inherit;
    font-size: 11px;
    font-weight: 500;
    border-radius: 50px;
    
    &:hover {
      background: transparent;
      transform: none;
    }
  }
`;

const CreateButtonIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0;
  transition: margin 0.3s ease;
  
  svg {
    color: white;
  }
  
  /* Add margin when sidebar is expanded */
  [data-sidebar-expanded="true"] & {
    margin-right: 0;
  }
  
  @media(max-width: 768px) {
    width: 24px;
    height: 24px;
    background: ${({theme}) => theme.colors?.primary || '#007bff'};
    border-radius: 6px;
    margin-right: 0;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const CreateButtonText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  opacity: 0;
  width: 0;
  transition: opacity 0.3s ease, width 0.3s ease, margin 0.3s ease;
  
  /* Show text when sidebar is hovered */
  [data-sidebar-expanded="true"] & {
    opacity: 1;
    width: auto;
  }
  
  @media(max-width: 768px) {
    opacity: 1;
    width: auto;
    font-size: 11px;
    color: ${({theme}) => theme.colors?.primary || '#007bff'};
  }
`;

// Animation keyframes (same as FloatingCreateButton)
const slideUpFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Create Options - transparent overlay just for click detection
const CreateOptionsOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: transparent;
`;

// Desktop: positioned to the right of sidebar, aligned with Create button
// Mobile: positioned above the create button in bottom nav
const CreateOptionsContainer = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 9999;
  
  /* Desktop - position to the right of sidebar, vertically centered with Create button */
  @media(min-width: 769px) {
    left: calc(var(--sidebar-collapsed-width) + 16px);
    top: 50%;
    transform: translateY(-50%);
  }
  
  /* Mobile - position above the bottom nav, horizontal layout */
  @media(max-width: 768px) {
    flex-direction: row;
    gap: 24px;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom) + 20px);
    background: ${({theme}) => theme.colors?.bg2 || 'rgba(26, 26, 26, 0.95)'};
    padding: 16px 24px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CreateOptionButton = styled(UnstyledButton)`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  animation: ${slideUpFadeIn} 0.3s ease forwards;
  animation-delay: ${props => props.$delay || 0}ms;
  opacity: 0;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const PostOptionButton = styled(CreateOptionButton)`
  background: ${({theme}) => theme.colors?.primaryPink || '#ec4899'};
  color: white;
  
  &:hover {
    background: ${({theme}) => theme.colors?.primaryPinkLight || '#f472b6'};
  }
`;

const TargetOptionButton = styled(CreateOptionButton)`
  background: ${({theme}) => theme.colors?.primaryBlue || '#3b82f6'};
  color: white;
  
  &:hover {
    background: ${({theme}) => theme.colors?.primaryBlueLight || '#60a5fa'};
  }
`;

const PollOptionButton = styled(CreateOptionButton)`
  background: ${({theme}) => theme.colors?.primaryPinkDark || '#be185d'};
  color: white;
  
  &:hover {
    background: ${({theme}) => theme.colors?.primaryBlueDark || '#2563eb'};
  }
`;

const VibeOptionButton = styled(CreateOptionButton)`
  background: linear-gradient(135deg,
    ${({theme}) => theme.colors?.primaryPink || '#ec4899'},
    ${({theme}) => theme.colors?.primaryBlue || '#3b82f6'});
  color: white;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  z-index: 10000;
  
  @media(max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Tooltip = styled.div`
  position: absolute;
  left: 60px;
  white-space: nowrap;
  background: ${({theme}) => theme.colors?.bg2 || '#1a1a1a'};
  color: ${({theme}) => theme.colors?.text || 'white'};
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  cursor: pointer;
  
  @media(max-width: 768px) {
    position: static;
    background: transparent;
    box-shadow: none;
    padding: 4px 0 0 0;
    font-size: 11px;
    color: ${({theme}) => theme.colors?.text || 'white'};
  }
`;

const CreateButtonWrapper = styled.div`
  position: relative;
  width: 100%;
  
  @media(max-width: 768px) {
    position: static;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const ModalContent = styled.div`
  background: ${({theme}) => theme.colors?.bg2 || '#1a1a1a'};
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);

  @media (max-width: 480px) {
    max-height: 95vh;
    border-radius: 12px;
  }
`;

const postTypes = [
  { type: 'POST', icon: 'Post', label: 'Post', Component: PostOptionButton, delay: 0, requiresPaidContent: false },
  { type: 'VIBE', icon: 'Vibe', label: 'Vibe', Component: VibeOptionButton, delay: 40, requiresPaidContent: false },
  { type: 'TARGET', icon: 'Target', label: 'Goal', Component: TargetOptionButton, delay: 80, requiresPaidContent: true },
  { type: 'POLL', icon: 'CreatePoll', label: 'Poll', Component: PollOptionButton, delay: 120, requiresPaidContent: false }
];

// ==================== DESKTOP "MORE" TOGGLE ====================

const MoreToggleButton = styled(UnstyledButton)`
  display: none;

  @media(min-width: 769px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    padding: ${v.smSpacing};
    margin-bottom: 2px;
    border: none;
    border-left: 3px solid transparent;
    background: transparent;
    color: ${({theme}) => theme.colors.primary};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    font: inherit;

    [data-sidebar-expanded="true"] & {
      padding: ${v.smSpacing} ${v.mdSpacing};
      justify-content: space-between;
    }

    &:hover {
      background: ${({theme}) => theme.colors.primaryLight};
    }

    .more-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .more-label {
      display: none;
      font-size: 13px;
      font-weight: 500;
      text-align: left;
      flex: 1;
      margin-left: 8px;
      white-space: nowrap;
    }

    .more-chevron {
      display: none;
      transition: transform 0.2s ease;
      /* Base icon is ChevronRight (points right). Rotate -90deg when closed so it points up. */
      transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
    }

    [data-sidebar-expanded="true"] & .more-label {
      display: block;
    }

    [data-sidebar-expanded="true"] & .more-chevron {
      display: flex;
    }
  }
`;

// ==================== DESKTOP "MORE" POPOVER (anchored) ====================

const popoverFadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const MorePopover = styled.div`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  min-width: 240px;
  max-height: ${({ $maxHeight }) => ($maxHeight ? `${$maxHeight}px` : 'calc(100vh - 32px)')};
  overflow-y: auto;
  /* Layer theme tint over an opaque fallback so the popover is fully solid
     regardless of theme (theme colors are alpha-blended and rely on
     backdrop-filter to look opaque, which we can't use here). */
  background:
    linear-gradient(${({ theme }) => theme.colors.bg2 || 'rgba(30,30,30,0.85)'},
                    ${({ theme }) => theme.colors.bg2 || 'rgba(30,30,30,0.85)'}),
    ${({ theme }) => theme.colors.background || '#1a1a1a'};
  background-color: ${({ theme }) => theme.colors.background || '#1a1a1a'};
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: 1px solid ${({ theme }) => theme.colors.border || 'rgba(255, 255, 255, 0.12)'};
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.04);
  z-index: 2147483646;
  animation: ${popoverFadeIn} 0.14s cubic-bezier(0.2, 0, 0.2, 1);
  transform-origin: ${({ $flipY }) => ($flipY ? 'bottom left' : 'top left')};
  visibility: ${({ $ready }) => ($ready ? 'visible' : 'hidden')};
`;

const MorePopoverItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: ${({ theme, $isActive }) => ($isActive ? theme.button.primaryText : theme.colors.primary)};
  background: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : 'transparent')};
  background-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : 'transparent')};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;

  svg {
    color: inherit;
  }

  &:hover {
    background: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primary : theme.colors.primaryLight || 'rgba(255,255,255,0.08)'};
    color: ${({ theme }) => theme.button.primaryText};
  }

  &:hover svg {
    color: ${({ theme }) => theme.button.primaryText};
  }
`;

const MorePopoverIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const Header = ({ isLoggedIn, onLogout, onDesktopMoreToggle }) => {
    const { currentUser } = useAuth();
    const { hideBottomNavigation } = useNavigation();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
    const [showCreateOptions, setShowCreateOptions] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedPostType, setSelectedPostType] = useState('POST');
    const [showVibeComposer, setShowVibeComposer] = useState(false);
    const [showDesktopMore, setShowDesktopMore] = useState(false);
    const moreToggleRef = useRef(null);
    const morePopoverRef = useRef(null);
    const [moreAnchor, setMoreAnchor] = useState({ top: 0, left: 0, maxHeight: null, flipY: false, ready: false });

    const updateMoreAnchor = useCallback(() => {
      const btn = moreToggleRef.current;
      if (!btn) return;
      const btnRect = btn.getBoundingClientRect();
      const popover = morePopoverRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 8;
      const gap = 8;

      // Fallback preferred size before we can measure the popover on first paint
      const popW = popover ? popover.offsetWidth : 260;
      const popH = popover ? popover.offsetHeight : 320;

      // Preferred: to the right of button, top-aligned with button
      let left = btnRect.right + gap;
      let top = btnRect.top;

      // Horizontal overflow: flip to the left of the button, else clamp to viewport
      if (left + popW + margin > vw) {
        const flippedLeft = btnRect.left - gap - popW;
        left = flippedLeft >= margin ? flippedLeft : Math.max(margin, vw - popW - margin);
      }

      // Vertical: if it would overflow the bottom, align the popover's bottom with the button's bottom
      let flipY = false;
      let maxHeight = vh - margin * 2;
      if (top + popH + margin > vh) {
        const flippedTop = btnRect.bottom - popH;
        if (flippedTop >= margin) {
          top = flippedTop;
          flipY = true;
        } else {
          // Neither above nor below fits fully: pin near the bottom and cap height
          top = margin;
          maxHeight = vh - margin * 2;
        }
      }

      setMoreAnchor({ top, left, maxHeight, flipY, ready: true });
    }, []);

    useEffect(() => {
      onDesktopMoreToggle?.(showDesktopMore);
    }, [showDesktopMore, onDesktopMoreToggle]);

    useEffect(() => {
      if (!showDesktopMore) {
        setMoreAnchor((a) => ({ ...a, ready: false }));
        return undefined;
      }
      // First-pass position (before we can measure the popover)
      updateMoreAnchor();
      // Second pass once the popover is in the DOM so we know its real size
      const rafId = requestAnimationFrame(updateMoreAnchor);
      const onOutside = (e) => {
        if (
          morePopoverRef.current && !morePopoverRef.current.contains(e.target) &&
          moreToggleRef.current && !moreToggleRef.current.contains(e.target)
        ) {
          setShowDesktopMore(false);
        }
      };
      const onKey = (e) => {
        if (e.key === 'Escape') setShowDesktopMore(false);
      };
      window.addEventListener('mousedown', onOutside);
      window.addEventListener('keydown', onKey);
      window.addEventListener('resize', updateMoreAnchor);
      window.addEventListener('scroll', updateMoreAnchor, true);
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener('mousedown', onOutside);
        window.removeEventListener('keydown', onKey);
        window.removeEventListener('resize', updateMoreAnchor);
        window.removeEventListener('scroll', updateMoreAnchor, true);
      };
    }, [showDesktopMore, updateMoreAnchor]);

    const handleCreateClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowCreateOptions(!showCreateOptions);
    };

    const handleTypeSelect = (type) => {
        setShowCreateOptions(false);
        if (type === 'VIBE') {
            setShowVibeComposer(true);
            return;
        }
        setSelectedPostType(type);
        setShowCreateModal(true);
    };

    const handlePostCreated = (newPost) => {
        setShowCreateModal(false);
        if (newPost?.id) {
            navigate(`${SINGLE_POST_PAGE_URL}/${newPost.id}`);
        }
    };

    // Mobile navigation - Home, Search, Notifications, Create (handled separately), Messages, Profile Avatar (More)
    const mobileNavigationLinks = useMemo(() => {
        return [
            {
                label: "Home",
                icon: <VipIcon name="Home" size={20} />,
                to: HOME_PAGE_URL,
                notification: 0,
            },
            {
                label: "Search",
                icon: <VipIcon name="Search" size={20} />,
                to: SEARCH_PAGE_URL,
                notification: 0,
            },
            {
                label: "Notifications",
                icon: (
                    <NotificationBadge>
                        <VipIcon name="Notifications" size={20} />
                    </NotificationBadge>
                ),
                to: NOTIFICATIONS_PAGE_URL,
                notification: 0,
            }
            // Create button and Messages are added separately in the JSX
            // Profile avatar with More menu is added at the end
        ];
    }, []);

    // Desktop navigation - Home, Notifications, Messages, Create (separate), Dashboard, Planned Releases, Circles, Settings, Profile, Logout
    const desktopNavigationLinks = useMemo(() => {
        const links = [
            {
                label: "Home",
                icon: <VipIcon name="Home" size={20} />,
                to: HOME_PAGE_URL,
                notification: 0,
            },
            {
                label: "Notifications",
                icon: (
                    <NotificationBadge>
                        <VipIcon name="Notifications" size={20} />
                    </NotificationBadge>
                ),
                to: NOTIFICATIONS_PAGE_URL,
                notification: 0,
            },
            {
                label: "Messages",
                icon: (
                    <MessageBadge>
                        <VipIcon name="Messages" size={20} />
                    </MessageBadge>
                ),
                to: MESSAGE_PAGE_URL,
                notification: 1,
            }
            // Create button is added separately in the JSX after these links
        ];

        return links;
    }, []);

    // Helper to check accessSpec permissions
    const hasAccess = (feature) => {
        // If no accessSpec, use isCreator as default
        if (!currentUser?.accessSpec) {
            return currentUser?.creator ?? false;
        }
        return currentUser.accessSpec[feature] ?? false;
    };

    const handleContactSupport = useCallback(async () => {
        try {
            const conversation = await messageService.getSupportConversation();
            if (conversation?.id) {
                navigate(`${MESSAGE_PAGE_URL}/${conversation.id}`);
            } else {
                toast.error('Support is currently unavailable');
            }
        } catch (err) {
            if (err?.code === 'SUPPORT_UNAVAILABLE') {
                toast.error('Support is currently unavailable');
            } else {
                toast.error('Could not open support chat. Please try again.');
            }
        }
    }, [navigate]);

    // Desktop nav after Create button, split into 2 tiers to declutter:
    //   1. Primary: always visible.
    //   2. More: collapsed behind a "More" toggle.
    // Profile navigation + Logout are handled by SidebarProfileSection (HeaderLayout).
    const { desktopPrimaryLinks, desktopMoreLinks } = useMemo(() => {
        const primary = [];

        if (hasAccess('canAccessDashboard')) {
            primary.push({
                label: "Dashboard",
                icon: <VipIcon name="Dashboard" size={20} />,
                to: DASHBOARD_PAGE_URL,
                notification: 0,
            });
        }

        if (currentUser?.creator) {
            primary.push({
                label: "Planned Releases",
                icon: <VipIcon name="Clock" size={20} />,
                to: SCHEDULED_PAGE_URL,
                notification: 0,
            });
        }

        primary.push({
            label: "Circles",
            icon: <VipIcon name="Circles" size={20} />,
            to: CIRCLES_PAGE_URL,
            notification: 0,
        });

        // Non-creators get a direct way into creator onboarding (no `to`, so it is never highlighted as Settings)
        if (currentUser && !(currentUser.creator || currentUser.isCreator)) {
            primary.push({
                label: "Become a Creator",
                icon: <VipIcon name="Plus" size={20} />,
                action: () => navigate(SETTINGS_PAGE_URL, { state: { activeTab: 'creator' } }),
                notification: 0,
            });
        }

        const more = [
            ...(currentUser?.creator ? [{
                label: "Post Categories",
                icon: <VipIcon name="PostCategories" size={20} />,
                to: CATEGORIES_PAGE_URL,
            }] : []),
            ...(currentUser?.creator ? [{
                label: "Media Library",
                icon: <VipIcon name="Gallery" size={20} />,
                to: MEDIA_LIBRARY_PAGE_URL,
            }] : []),
            {
                label: "Owned Content",
                icon: <VipIcon name="ImageIcon" size={20} />,
                to: PURCHASES_PAGE_URL,
            },
            {
                label: "Subscriptions",
                icon: <VipIcon name="StarIcon" size={20} />,
                to: SUBSCRIPTIONS_PAGE_URL,
            },
            {
                label: "Settings",
                icon: <VipIcon name="Settings" size={20} />,
                to: SETTINGS_PAGE_URL,
            },
            {
                label: "Message Support",
                icon: <VipIcon name="Messages" size={20} />,
                action: handleContactSupport,
            },
            {
                label: "Help Center",
                icon: <VipIcon name="HelpSupport" size={20} />,
                to: HELP_DESK_URL,
                external: true,
            },
        ];

        return { desktopPrimaryLinks: primary, desktopMoreLinks: more };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, isLoggedIn]);

    const handleMoreClick = (e) => {
        e.preventDefault();
        setIsMoreMenuOpen(!isMoreMenuOpen);
    };

    const handleMoreMenuClose = () => {
        setIsMoreMenuOpen(false);
    };

    const handleClick = (action, to, external, e) => {
        if (action) {
            e.preventDefault();
            action();
        } else if (external) {
            e.preventDefault();
            window.open(to, '_blank', 'noopener,noreferrer');
        }
        // For regular navigation links, let React Router handle it naturally
    };

    return (
        <HeaderLinkContainer $hideBottomNavigation={hideBottomNavigation}>
            <HeaderIcons>
                {/* Desktop: Show all navigation items */}
                <div className="desktop-nav" style={{ display: 'contents' }}>
                    {/* Home, Search, Notifications, Messages */}
                    {desktopNavigationLinks.map(({icon, label, to, action, external}) => (
                        <HeaderLink 
                            key={label} 
                            $isActive={pathname === to} 
                            to={to || '#'}
                            onClick={(e) => handleClick(action, to, external, e)}
                        >
                            <HeaderLinkIcon $isActive={pathname === to}>
                                {icon}
                            </HeaderLinkIcon>
                            <HeaderLinkText $isActive={pathname === to}>
                                {label}
                            </HeaderLinkText>
                        </HeaderLink>
                    ))}
                    
                    {/* Create Post Button - Desktop */}
                    <CreateButtonWrapper>
                        <CreateButton onClick={handleCreateClick}>
                            <CreateButtonIcon>
                                <VipIcon name={showCreateOptions ? 'CloseXCircle' : 'Plus'} size={20} stroke="white" />
                            </CreateButtonIcon>
                            <CreateButtonText>Create Post</CreateButtonText>
                        </CreateButton>
                    </CreateButtonWrapper>

                    {/* Primary destinations (always visible) */}
                    {desktopPrimaryLinks.map(({icon, label, to, action, external}) => (
                        <HeaderLink
                            key={label}
                            $isActive={pathname === to}
                            to={to || '#'}
                            onClick={(e) => handleClick(action, to, external, e)}
                        >
                            <HeaderLinkIcon $isActive={pathname === to}>
                                {icon}
                            </HeaderLinkIcon>
                            <HeaderLinkText $isActive={pathname === to}>
                                {label}
                            </HeaderLinkText>
                        </HeaderLink>
                    ))}

                    {/* "More" popover trigger */}
                    {desktopMoreLinks.length > 0 && (
                        <MoreToggleButton
                            ref={moreToggleRef}
                            type="button"
                            $open={showDesktopMore}
                            onClick={() => setShowDesktopMore((v) => !v)}
                            aria-expanded={showDesktopMore}
                            aria-haspopup="menu"
                            aria-label={showDesktopMore ? 'Hide more' : 'Show more'}
                        >
                            <div className="more-icon">
                                <VipIcon name="DotsHorizontal" size={20} />
                            </div>
                            <span className="more-label">More</span>
                            <div className="more-chevron">
                                <VipIcon name="ChevronRight" size={14} />
                            </div>
                        </MoreToggleButton>
                    )}

                </div>

                {/* Mobile: Home, Search, Notifications, Create, Messages, Profile Avatar */}
                <div className="mobile-nav" style={{ display: 'contents' }}>
                    {/* Home, Search, Notifications */}
                    {mobileNavigationLinks.map(({icon, label, to}) => (
                        <HeaderLink 
                            key={label} 
                            $isActive={pathname === to} 
                            to={to}
                        >
                            <HeaderLinkIcon $isActive={pathname === to}>
                                {icon}
                            </HeaderLinkIcon>
                            <HeaderLinkText $isActive={pathname === to}>
                                {label}
                            </HeaderLinkText>
                        </HeaderLink>
                    ))}
                    
                    {/* Create Button - Mobile */}
                    <CreateButtonWrapper>
                        <CreateButton onClick={handleCreateClick}>
                            <CreateButtonIcon>
                                <VipIcon name={showCreateOptions ? 'CloseXCircle' : 'Plus'} size={16} stroke="white" />
                            </CreateButtonIcon>
                        </CreateButton>
                    </CreateButtonWrapper>
                    
                    {/* Messages - Mobile */}
                    <HeaderLink 
                        $isActive={pathname === MESSAGE_PAGE_URL} 
                        to={MESSAGE_PAGE_URL}
                    >
                        <HeaderLinkIcon $isActive={pathname === MESSAGE_PAGE_URL}>
                            <MessageBadge>
                                <VipIcon name="Messages" size={20} />
                            </MessageBadge>
                        </HeaderLinkIcon>
                        <HeaderLinkText $isActive={pathname === MESSAGE_PAGE_URL}>
                            Messages
                        </HeaderLinkText>
                    </HeaderLink>
                    
                    {/* Profile Avatar / More Menu Button - Mobile Only */}
                    <MoreButton
                        $isActive={isMoreMenuOpen}
                        onClick={handleMoreClick}
                    >
                        <HeaderLinkIcon>
                            <ProfileImage 
                                user={currentUser}
                                size="28px"
                                hideOnlineIndicator={true}
                            />
                        </HeaderLinkIcon>
                    </MoreButton>
                </div>
            </HeaderIcons>
            
            {/* Create Options Overlay and Container - rendered via portal */}
            {showCreateOptions && ReactDOM.createPortal(
                <>
                    <CreateOptionsOverlay onClick={() => setShowCreateOptions(false)} />
                    <CreateOptionsContainer>
                        {postTypes
                            .filter(pt => !pt.requiresPaidContent || hasAccess('canCreatePaidContent'))
                            .map(({ type, icon, label, Component, delay }) => (
                            <TooltipContainer key={type} onClick={() => handleTypeSelect(type)}>
                                <Component 
                                    $delay={delay}
                                    title={label}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleTypeSelect(type);
                                    }}
                                >
                                    <VipIcon name={icon} size={24} />
                                </Component>
                                <Tooltip onClick={(e) => {
                                    e.stopPropagation();
                                    handleTypeSelect(type);
                                }}>{label}</Tooltip>
                            </TooltipContainer>
                        ))}
                    </CreateOptionsContainer>
                </>,
                document.body
            )}
            
            {/* More Menu Component - Mobile Only (shows Dashboard, Settings, Profile, Logout) */}
            <MoreMenu 
                isOpen={isMoreMenuOpen}
                onClose={handleMoreMenuClose}
                onLogout={onLogout}
            />

            {/* Desktop "More" popover — anchored to sidebar button */}
            {showDesktopMore && desktopMoreLinks.length > 0 && createPortal(
                <MorePopover
                    ref={morePopoverRef}
                    role="menu"
                    $top={moreAnchor.top}
                    $left={moreAnchor.left}
                    $maxHeight={moreAnchor.maxHeight}
                    $flipY={moreAnchor.flipY}
                    $ready={moreAnchor.ready}
                >
                    {desktopMoreLinks.map(({ icon, label, to, action, external }) => (
                        <MorePopoverItem
                            key={label}
                            role="menuitem"
                            to={to || '#'}
                            $isActive={pathname === to}
                            onClick={(e) => {
                                handleClick(action, to, external, e);
                                setShowDesktopMore(false);
                            }}
                        >
                            <MorePopoverIcon>{icon}</MorePopoverIcon>
                            {label}
                        </MorePopoverItem>
                    ))}
                </MorePopover>,
                document.body
            )}
            
            {/* Create Post Modal */}
            {showCreateModal && ReactDOM.createPortal(
                <ModalOverlay>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CreatePost 
                            onPostCreated={handlePostCreated} 
                            defaultPostType={selectedPostType}
                            onRequestClose={() => setShowCreateModal(false)}
                            showHeader={true}
                        />
                    </ModalContent>
                </ModalOverlay>,
                document.body
            )}

            {/* Vibe Composer */}
            {showVibeComposer && (
                <VibeComposer
                    onCreated={() => setShowVibeComposer(false)}
                    onClose={() => setShowVibeComposer(false)}
                />
            )}
        </HeaderLinkContainer>
    );
};

export default Header;